<script>

// utilities
import formatMoney from '../utilities/formatMoney';

export default {
    name: 'semesterTuitionRules',

    props: {
        term: null,
        school: null,
        degree: null,
        yearOfStudy: null,
        termsMap: null,
        numOfCredits: null,
        attendanceStatus: null
    },

    data() {
        return {
            activeTuitionRule: null,
            numOfCreditsBuffer: this.numOfCredits,
            attendanceStatusBuffer: this.attendanceStatus,
        }
    },

    computed: {
        termText: function () {
            return this.term === "fullyear" ? "year" : "semester";
        },
        minCredits: function () {
            return this.activeTuitionRule ? this.activeTuitionRule.creditRangeMin : (this.degree.creditRangeMin) ? this.degree.creditRangeMin : 3;
        },
        maxCredits: function () {
            return this.activeTuitionRule ? this.activeTuitionRule.creditRangeMax : (this.degree.creditRangeMax) ? this.degree.creditRangeMax : 15;
        },
        creditRangeSliderWidth: function () {
            const range = this.maxCredits - this.minCredits;
            let width = range * 11;
            width = (width > 50) ? width : 50;
            return width + 'px';
        },
        isPerCredit: function () {
            return (
                this.askForCredits
                || this.activeTuitionRule
                && (this.activeTuitionRule.creditRangeMin === this.activeTuitionRule.creditRangeMax)
                && this.activeTuitionRule.tuitionMethod !== 'fullTime'
            );
        },
        askForCredits: function () {

            // rules take precedence: does the current rule require asking for # credits
            if (this.activeTuitionRule) {
                if (this.activeTuitionRule.tuitionMethod === 'perCredit') {
                    return this.activeTuitionRule.creditRangeMin !== this.activeTuitionRule.creditRangeMax;
                } else if (this.activeTuitionRule.tuitionMethod === 'fullTime') {
                    return false
                }
            }

            // otherwise check the basic degree info
            return this.attendanceStatus === 'parttime' || !this.degree.fullTimeTuition

        },
        askPartTimeFullTime: function () {

            return (
                (
                    // no rule has been set
                    !this.activeTuitionRule
                    // we don't have to ask which year first
                    && !this.askWhichYear
                    // is there a fulltime tuition set
                    && this.degree.fullTimeTuition
                    // and a parttime rate set
                    && this.degree.perCreditTuition
                    // and it is explicity allowed
                    && this.degree.allowPartTime
                )
            );

        },
        isTuitionReady: function() {
            return (this.askPartTimeFullTime && this.attendanceStatus != "0") || !this.askPartTimeFullTime
        },
        tuition: function() {

            let tuition = 0;
            if (this.attendanceStatus === 0) return 0

            if (this.isPerCredit) {
                tuition = this.numOfCredits * parseInt(this.degree.perCreditTuition)
            } else {
                tuition = parseInt(this.degree.fullTimeTuition)
            }

            return tuition
        },
        tuitionDetailsMessage: function () {

            if (!this.isTuitionReady) return false

            if (this.isPerCredit) {
                return '<p class="tf-tuitionMessage">Tuition for the ' + this.termsMap[this.term].label + ' is <b>' + this.formatMoney(this.tuition) + '</b>.</p><p class="tf-smallNote">' + this.numOfCredits + ' credit' + (this.numOfCredits > 1 ? 's' : '') + ' at ' + this.formatMoney(this.degree.perCreditTuition) + ' per credit' + '</p>';
            } else {
                return '<p class="tf-tuitionMessage">Full time tuition for the ' + this.termsMap[this.term].label + ' is <b>' + this.formatMoney(this.tuition) + '</b>.</p>';
            }

        },

    },

    watch : {
        numOfCredits() {
            this.numOfCreditsBuffer = this.numOfCredits;
        },
        attendanceStatus() {
            this.attendanceStatusBuffer = this.attendanceStatus;
        },
        tuition() {
            this.updateTuition();
        },
        yearOfStudy() {
            this.setDegreeRule();
        }
    },

    methods: {
        formatMoney,
        setDegreeRule() {

            // Note: removing this fixed an error that caused rules not to be applied
            // if (!this.yearOfStudy) return false

            const _self = this;
            let applicableRules = [];

            // find all rules for the year of study that also match the term we're calculating for
            applicableRules = this.degree.rules.filter( (rule) => {

                let yearMatch = false;

                if (rule.yearOfStudy !== 'any') {

                    switch (rule.yearOperator) {
                        case '=':
                            // fuzzy for integer/string matches
                            yearMatch = (rule.yearOfStudy == _self.yearOfStudy);
                            break;

                        case '<':
                            yearMatch = (rule.yearOfStudy < _self.yearOfStudy);
                            break;

                        case '>':
                            yearMatch = (rule.yearOfStudy > _self.yearOfStudy);
                            break;
                    }

                } else {
                    yearMatch = true;
                }

                return (
                    yearMatch && ['any', _self.term].includes(rule.term)
                );

            });

            // return 1st match
            this.activeTuitionRule = applicableRules[0] || null;

            if (this.activeTuitionRule) {
                // TODO: an attempt to preset, but maybe not necessary
                if (this.activeTuitionRule.creditRangeMin === this.activeTuitionRule.creditRangeMax) {
                    this.numOfCreditsBuffer = this.activeTuitionRule.creditRangeMin;
                }
                this.attendanceStatusBuffer = this.activeTuitionRule.tuitionMethod
                this.update()
            }

        },
        updateTuition() {
            this.$emit('update-tuition', {
                value: this.tuition,
                term: this.term,
            })
        },
        update() {

            const data = {
                term: this.term,
                numOfCredits: this.numOfCreditsBuffer,
                attendanceStatus: this.attendanceStatusBuffer
            }

            this.$emit('update', data);
        }

    },

    mounted() {
        this.setDegreeRule()

        // if there is no fulltime tuition, set the status at part-time
        if (!this.degree.fullTimeTuition) {
            this.attendanceStatusBuffer = 'parttime'
            this.numOfCreditsBuffer = this.minCredits
        }

        if (!this.degree.allowPartTime) {
            this.attendanceStatusBuffer = 'fulltime'
        }

        this.update()
        this.updateTuition()
    },

    updated() {
        this.setDegreeRule()
    },

    beforeDestroy() {


    }

}
</script>

<template>

    <div class="tf tf--calcBox">

        <div class="tf-semesterLabel" >{{termsMap[this.term].label}}</div>

        <!-- ask if FT / PT if is an option -->
        <div v-if="askPartTimeFullTime">
            <label class="tf-selectorLabel" for="attendanceStatus">Will you attend part-time or full-time?</label>
            <select v-model="attendanceStatusBuffer" id="attendanceStatus" class="tf-selector" @change="update">
                <option v-bind:value="0">Select a Status</option>
                <option value="fulltime">Full time</option>
                <option value="parttime">Part time</option>
            </select>
        </div>

        <!-- ask how many credits if necessary -->
        <div v-if="askForCredits">
            <label class="tf-selectorLabel">How many credits will you take?</label>
            <input class="tf-range" type="range" v-model="numOfCreditsBuffer" @mouseup="update" id="numOfCredits"
                   name="numOfCredits"
                   :min="minCredits" :max="maxCredits"
                   v-bind:style="{ width: creditRangeSliderWidth }"
            >
            &nbsp;&nbsp;<span>{{ this.numOfCredits }}</span>
        </div>

        <!-- show tuition calc details message -->
        <div v-if="tuitionDetailsMessage" v-html="tuitionDetailsMessage"></div>

    </div>

</template>